import { DeleteOutlined, InboxOutlined } from "@ant-design/icons";
import {
  Checkbox,
  Col,
  Form,
  Grid,
  Image,
  Input,
  Radio,
  Row,
  Upload,
} from "antd";
import { RcFile } from "antd/lib/upload";
import ImgCrop from "antd-img-crop";
import { Button } from "components";
import { CompanySelect } from "components/main-filters/company-select";
import { RouteLeavingGuard } from "components/route-leaving-guard/route-leaving-guard";
import { AreaSelect } from "features/areas/components/area-select/area-select";
import { useUploadProps } from "hooks/use-upload-props";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Facility, PaymentMethod } from "types";
import { paymentMethodTranslationsMap } from "utils/transaction";
import { geolocationPattern } from "utils/validations";

import { UpdateFacilityField, UpdateFacilityFormValues } from "./types";
const { useBreakpoint } = Grid;
const { Dragger } = Upload;

type UpdateFacilityFormProps = {
  handleUpdate: (object: UpdateFacilityFormValues, FileList: RcFile[]) => void;
  handleDeleteImage: () => void;
  disabled: boolean;
  data: Facility;
};

export const UpdateFacilityForm = ({
  data,
  handleUpdate,
  handleDeleteImage,
  disabled,
}: UpdateFacilityFormProps) => {
  const formName = "update-facility-form";
  const screens = useBreakpoint();
  const [validateTrigger, setValidateTrigger] = useState(["onSubmit"]);
  const [form] = Form.useForm<UpdateFacilityFormValues>();
  const { t } = useTranslation();
  const { fileList, uploadProps } = useUploadProps();
  const [isFormDirty, setIsFormDirty] = useState(false);

  useEffect(() => {
    if (fileList.length) setIsFormDirty(true);
  }, [fileList]);

  const formItemLayout = screens.lg
    ? { labelCol: { span: 7 }, wrapperCol: { span: 10 } }
    : null;

  const companyGeolocation = data.geolocation
    ? `${data.geolocation.lat}, ${data.geolocation.lon}`
    : "";

  const getInitialValues = {
    [UpdateFacilityField.Company]: data.company_id,
    [UpdateFacilityField.CompanyName]: data.company_id,
    [UpdateFacilityField.Area]: data.area.id,
    [UpdateFacilityField.FacilityName]: data.name,
    [UpdateFacilityField.FacilityNameInArabic]: data.name_arabic,
    [UpdateFacilityField.Rank]: data.rank,
    [UpdateFacilityField.Geolocation]: companyGeolocation,
    [UpdateFacilityField.Image]: undefined,
    [UpdateFacilityField.Payment]: data.payment_methods,
    [UpdateFacilityField.splitpayment]: data.is_split_payment,
    [UpdateFacilityField.is_fixed_booking]: data.is_fixed_booking,
  };
  const paymentOptions = [
    {
      value: PaymentMethod.Cash,
      label: paymentMethodTranslationsMap[PaymentMethod.Cash],
    },
    {
      value: PaymentMethod.Knet,
      label: paymentMethodTranslationsMap[PaymentMethod.Knet],
    },
    {
      value: PaymentMethod.Wallet,
      label: paymentMethodTranslationsMap[PaymentMethod.Wallet],
    },
    {
      value: PaymentMethod.Credit,
      label: paymentMethodTranslationsMap[PaymentMethod.Credit],
    },
    {
      value: PaymentMethod.Apple,
      label: paymentMethodTranslationsMap[PaymentMethod.Apple],
    },
  ];

  const onFinish = async (formValues: UpdateFacilityFormValues) => {
    setIsFormDirty(false);
    // const file = fileList?.[0] as RcFile | undefined;
    handleUpdate(formValues, fileList as RcFile[]);
    // If no file uploaded → send update without image
    // if (!file) {
    //   handleUpdate(formValues, []);

    //   return;
    // }
    // handleUpdate(formValues, [file]);

    // const isValid = await beforeUploadValidateDimensions(file);
    // if (!isValid) return; // ⛔ Stop if invalid

    // // ✅ Allow only files between 0 and 5MB
    // const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
    // if (file.size > 0 && file.size <= maxSize) {
    // } else {
    //   message.error("File size must be between 0 and 5MB");
    // }
  };

  // const beforeUploadValidateDimensions = async (file: RcFile) => {
  //   const isValidDimensions = await new Promise<boolean>((resolve) => {
  //     const img = new window.Image();
  //     img.src = URL.createObjectURL(file);
  //     img.onload = () => {
  //       const width = img.width;
  //       const height = img.height;
  //       URL.revokeObjectURL(img.src);
  //       resolve(width === 640 && height === 322);
  //     };
  //   });

  //   if (!isValidDimensions) {
  //     message.error("Image must be exactly 640x360 pixels.");

  //     return Upload.LIST_IGNORE;
  //   }

  //   return true;
  // };

  return (
    <>
      {/* <div className='packForm'>
        <div className='warp_inner'> */}
      <Form<UpdateFacilityFormValues>
        {...formItemLayout}
        id={formName}
        name={formName}
        form={form}
        layout={screens.lg ? "horizontal" : "vertical"}
        initialValues={getInitialValues}
        onFinish={onFinish}
        onFinishFailed={() => {
          setValidateTrigger(["onChange"]);
        }}
        onFieldsChange={() => setIsFormDirty(true)}
        validateTrigger={validateTrigger}
        disabled={disabled}
      >
        {/* <Row> */}
        {/* <Col span={8}> */}
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.company.label")}
          // name={UpdateFacilityField.Company}
          name={UpdateFacilityField.CompanyName}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <CompanySelect
            theme={"standard"}
            companyType={undefined}
            bordered={true}
            style={{ maxWidth: "100%" }}
            placeholder={t("facilities.formFields.company.placeholder")}
          />
        </Form.Item>
        {/* </Col> */}
        {/* <Col span={8}> */}
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.area.label")}
          name={UpdateFacilityField.Area}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <AreaSelect
            placeholder={t("facilities.formFields.area.placeholder")}
          />
        </Form.Item>
        {/* </Col> */}
        {/* <Col span={8}> */}
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.facilityName.label")}
          name={UpdateFacilityField.FacilityName}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input
            placeholder={t("facilities.formFields.facilityName.placeholder")}
          />
        </Form.Item>
        {/* </Col> */}
        {/* <Col span={8}> */}
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.facilityArabic.label")}
          name={UpdateFacilityField.FacilityNameInArabic}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input
            placeholder={t("facilities.formFields.facilityArabic.placeholder")}
            dir="rtl"
          />
        </Form.Item>
        {/* </Col> */}
        {/* <Col span={8}> */}
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.rank.label")}
          name={UpdateFacilityField.Rank}
          rules={[
            {
              required: true,
            },
            () => ({
              validator(_, value) {
                if (value <= 0) {
                  return Promise.reject(
                    t("facilities.formFields.rank.errorLessThanOne")
                  );
                } else return Promise.resolve();
              },
            }),
          ]}
        >
          <Input placeholder={t("facilities.formFields.rank.placeholder")} />
        </Form.Item>
        {/* </Col> */}
        {/* <Col span={8}> */}
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.geolocation.label")}
          name={UpdateFacilityField.Geolocation}
          rules={[
            {
              required: true,
            },
            {
              pattern: geolocationPattern,
              message: t("validateMessages.custom.geolocation"),
            },
          ]}
        >
          <Input
            placeholder={t("facilities.formFields.geolocation.placeholder")}
          />
        </Form.Item>
        {/* </Col> */}
        {/* <Col span={9}> */}
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.payment.label")}
          name={UpdateFacilityField.Payment}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Checkbox.Group options={paymentOptions} />
        </Form.Item>
        {/* </Col> */}
        {/* <Col span={7}> */}
        <p
          className="note_payment"
          style={{
            color: "red",
            textAlign: "center",
            padding: "10px",
            marginRight: "200px",
            fontSize: "12px",
            paddingBottom: "24px",
            paddingTop: "0px",
          }}
        >
          * Cash payment is not allowed for app Fixed booking
        </p>
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.splitpayment.label")}
          name={UpdateFacilityField.splitpayment}
          rules={[
            {
              required: true,
            },
          ]}
        >
          {/* <Radio.Group>
            {SplitOptions.map((option) => (
              <Radio value={option.value} key={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group> */}
          <Radio.Group>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>
        {/* </Col> */}
        {/* <Col span={8}> */}
        <Form.Item<UpdateFacilityField>
          label={t("facilities.formFields.fixedbooking.label")}
          name={UpdateFacilityField.is_fixed_booking}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Radio.Group>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label={t("facilities.formFields.image.label")}
          getValueFromEvent={(event) => event?.fileList}
          rules={[
            {
              required: true,
            },
          ]}
        >
          {data.image_url ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Image
                width={300}
                style={{ borderRadius: "6px", objectFit: "contain" }}
                src={data.image_url}
                placeholder={true}
                preview={false}
              />
              <Button
                danger
                onClick={handleDeleteImage}
                style={{
                  borderColor: "red",
                  padding: "4px 10px",
                  marginTop: "8px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <DeleteOutlined />
                </div>
              </Button>
            </div>
          ) : fileList.length ? (
            <ImgCrop
              rotationSlider
              aspect={640 / 322}
              cropShape="rect"
              cropperProps={
                {
                  containerStyle: { width: 800, height: 425 },
                  cropAreaStyle: { border: "2px dashed #999" },
                  mediaStyle: {
                    maxHeight: "100%",
                    maxWeight: "100%",
                    objectFit: "contain", // ✅ important
                  },
                } as any
              }
            >
              <Upload
                {...uploadProps}
                name="file"
                accept=".png, .jpg, .jpeg"
                maxCount={1}
                listType="picture-card"
                showUploadList={{
                  showPreviewIcon: false,
                  showRemoveIcon: true,
                }}
              />
            </ImgCrop>
          ) : (
            <ImgCrop
              rotationSlider
              aspect={640 / 322} // 🔄 use same aspect for consistency
              cropShape="rect"
              cropperProps={
                {
                  containerStyle: { width: 800, height: 425 }, // 🔥 FIX: remove height: 0
                  cropAreaStyle: { border: "2px dashed #999" },
                  mediaStyle: {
                    maxHeight: "100%",
                    maxWeight: "100%",
                    objectFit: "contain",
                  },
                } as any
              }
            >
              <Dragger {...uploadProps} multiple={false}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">{t("upload.dragOrClick")}</p>
                <p className="ant-upload-hint1">
                  <Trans i18nKey="upload.hint1" components={{ br: <br /> }} />
                </p>
              </Dragger>
            </ImgCrop>
          )}
        </Form.Item>
        {/* </Col> */}
        {/* </Row> */}

        <Col span={screens.lg ? 24 : "auto"} offset={screens.lg ? 7 : 0}>
          <Row justify="end">
            <Button disabled={disabled} type="primary" htmlType="submit">
              {t("facilities.buttons.update")}
            </Button>
          </Row>
        </Col>
      </Form>
      {/* </div>
      </div> */}
      <RouteLeavingGuard blocked={isFormDirty} />
    </>
  );
};
